import {useEffect, useRef} from 'react'
import {Text} from '@primer/react-brand/esm'
import {animateUnscramble} from '../../_utils/unscramble'

import Styles from './Eyebrow.module.css'

type Props = {
  text: string
  hideIcon?: boolean
  className?: string
}

export function Eyebrow(props: Props) {
  const {text, hideIcon, className} = props
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLSpanElement>(null)
  const iconRef = useRef<HTMLSpanElement>(null)
  const hasAnimatedRef = useRef(false)
  const originalTextRef = useRef(text)

  useEffect(() => {
    // Only animate when icon is present
    if (hideIcon) return

    if (!containerRef.current) return

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          // Trigger animation when element enters viewport and hasn't animated yet
          if (entry.isIntersecting && !hasAnimatedRef.current) {
            hasAnimatedRef.current = true

            if (!textRef.current || !iconRef.current) return

            animateUnscramble({
              textElement: textRef.current,
              iconElement: iconRef.current,
              originalText: originalTextRef.current,
            })
          }
        }
      },
      {
        threshold: 0.5,
      },
    )

    observer.observe(containerRef.current)

    return () => {
      observer.disconnect()
    }
  }, [hideIcon])

  return (
    <div ref={containerRef} className={className}>
      {/* [a11y] Makes sure text stays available during unscramble animation */}
      <span className="sr-only">{text}</span>

      <Text className={Styles.Eyebrow} as="div" aria-hidden="true">
        <span ref={textRef}>{text}</span>
        {!hideIcon && <span ref={iconRef} className={Styles.Icon} />}
      </Text>
    </div>
  )
}
